import { type Language, TEXT_LIMITS } from '@posilka/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAddSavedAddress,
  useAddSavedRecipient,
  useSavedAddresses,
  useRemoveSaved,
  useSavedRecipients,
} from '@posilka/client/api/saved';
import { useCities } from '@posilka/client/api/orders';
import { cityName } from '@posilka/client/lib/cities';
import { Section } from '@/components/Section';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { haptic } from '@/lib/telegram';

interface Place {
  address: string;
  lat: number;
  lng: number;
}

/** Saqlanganlar chiplari + joriy qiymatni nom bilan saqlash (bir xil naqsh manzil va qabul qiluvchi uchun). */
function Bar<T extends { id: string; label: string }>({
  items,
  isCurrent,
  onPick,
  canSave,
  onSave,
  saving,
  error,
}: {
  items: T[];
  isCurrent: (item: T) => boolean;
  onPick: (item: T) => void;
  canSave: boolean;
  onSave: (label: string, done: () => void) => void;
  saving: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  const [naming, setNaming] = useState(false);
  const [label, setLabel] = useState('');
  const alreadySaved = items.some(isCurrent);

  return (
    <div className="mb-2 space-y-2">
      {(items.length > 0 || (canSave && !alreadySaved)) && (
        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                haptic.tap();
                onPick(item);
              }}
              className={`press shrink-0 rounded-full px-3.5 py-1.5 text-sm ${
                isCurrent(item)
                  ? 'border border-lime bg-lime font-semibold text-lime-ink'
                  : 'border border-line bg-surface'
              }`}
            >
              📌 {item.label}
            </button>
          ))}
          {canSave && !alreadySaved && !naming && (
            <button
              type="button"
              onClick={() => setNaming(true)}
              className="shrink-0 rounded-full border border-dashed border-line px-3.5 py-1.5 text-sm font-semibold text-link"
            >
              💾 {t('saved.save')}
            </button>
          )}
        </div>
      )}
      {naming && (
        <div className="flex gap-2">
          <input
            className="input py-2"
            autoFocus
            maxLength={TEXT_LIMITS.savedLabel.max}
            placeholder={t('saved.labelPlaceholder')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <button
            type="button"
            className="btn-secondary shrink-0"
            disabled={!label.trim() || saving}
            onClick={() =>
              onSave(label.trim(), () => {
                haptic.success();
                setNaming(false);
                setLabel('');
              })
            }
          >
            {t('common.save')}
          </button>
        </div>
      )}
      {error && <p className="px-1 text-sm text-destructive">{error}</p>}
    </div>
  );
}

export function SavedAddressBar({
  cityId,
  value,
  onPick,
}: {
  cityId: string;
  value: Place | undefined;
  onPick: (place: Place) => void;
}) {
  const errorMessage = useErrorMessage();
  const q = useSavedAddresses();
  const add = useAddSavedAddress();
  const items = (q.data ?? []).filter((a) => a.cityId === cityId);
  return (
    <Bar
      items={items}
      isCurrent={(a) => a.address === value?.address.trim()}
      onPick={(a) => onPick({ address: a.address, lat: a.lat, lng: a.lng })}
      canSave={(value?.address.trim().length ?? 0) >= 3}
      saving={add.isPending}
      error={add.isError ? errorMessage(add.error) : null}
      onSave={(label, done) =>
        value &&
        add.mutate(
          { label, cityId, address: value.address.trim(), lat: value.lat, lng: value.lng },
          { onSuccess: done, onError: () => haptic.error() },
        )
      }
    />
  );
}

export function SavedRecipientBar({
  name,
  phone,
  onPick,
}: {
  name: string;
  /** 9 raqam, +998 siz. */
  phone: string;
  onPick: (name: string, phone: string) => void;
}) {
  const errorMessage = useErrorMessage();
  const q = useSavedRecipients();
  const add = useAddSavedRecipient();
  return (
    <Bar
      items={q.data ?? []}
      isCurrent={(r) => r.phone === `+998${phone}` && r.name === name.trim()}
      onPick={(r) => onPick(r.name, r.phone.replace(/^\+998/, ''))}
      canSave={name.trim().length >= 2 && phone.length === 9}
      saving={add.isPending}
      error={add.isError ? errorMessage(add.error) : null}
      onSave={(label, done) =>
        add.mutate(
          { label, name: name.trim(), phone: `+998${phone}` },
          { onSuccess: done, onError: () => haptic.error() },
        )
      }
    />
  );
}

/** Profil: saqlangan manzil va qabul qiluvchilar, o'chirish bilan. */
export function SavedList() {
  const { t, i18n } = useTranslation();
  const addresses = useSavedAddresses();
  const recipients = useSavedRecipients();
  const removeAddress = useRemoveSaved('addresses');
  const removeRecipient = useRemoveSaved('recipients');
  const cities = useCities();
  const cityById = new Map((cities.data ?? []).map((c) => [c.id, c]));
  const rows = [
    ...(addresses.data ?? []).map((a) => {
      const c = cityById.get(a.cityId);
      return {
        id: a.id,
        label: a.label,
        detail: `${c ? cityName(c, i18n.language as Language) : ''} · ${a.address}`,
        remove: () => removeAddress.mutate(a.id),
      };
    }),
    ...(recipients.data ?? []).map((r) => ({
      id: r.id,
      label: r.label,
      detail: `👤 ${r.name} · ${r.phone}`,
      remove: () => removeRecipient.mutate(r.id),
    })),
  ];

  return (
    <Section title={t('saved.title')}>
      {rows.length === 0 ? (
        <p className="px-5 py-4 text-sm text-hint">{t('saved.empty')}</p>
      ) : (
        rows.map((r) => (
          <div
            key={r.id}
            className="flex items-center gap-3 border-b border-line px-5 py-2.5 last:border-b-0"
          >
            <div className="min-w-0 flex-1">
              <p className="font-medium">📌 {r.label}</p>
              <p className="truncate text-sm text-hint">{r.detail}</p>
            </div>
            <button
              type="button"
              aria-label={t('saved.delete')}
              className="shrink-0 px-2 text-destructive"
              onClick={() => {
                haptic.tap();
                r.remove();
              }}
            >
              ✕
            </button>
          </div>
        ))
      )}
    </Section>
  );
}
